import {
  applyInventoryHold,
  resolveInventoryHoldTargets,
  sameInventorySnapshot,
} from '../features/simulation/liveInventoryHold';
import type {
  AmhsLiveInventory,
  AmhsLiveInventoryGate,
  AmhsLiveInventoryStocker,
  AmhsStockerDropSummary,
} from '../types/amhsStream';
import { liveMotionStore } from './liveMotionStore';
import { liveOhtRuntimeStore } from './liveOhtRuntimeStore';
import { transferAnimationPipelineStore } from './transferAnimationPipelineStore';

type Listener = () => void;
const RUNTIME_RECOMPUTE_INTERVAL_MS = 100;

/**
 * Publishes the inventory the canvas should render: the raw stream frame, with
 * stockers and gates that are mid-handoff pinned until the OHT animation lands.
 * The scheduler keeps consuming the raw frame — gating its input would feed the
 * hold back into the thing that decides the hold.
 */
class LiveInventoryDisplayStore {
  private raw: AmhsLiveInventory | null = null;
  private lastStockerDrops: Record<string, AmhsStockerDropSummary> = {};
  private display: AmhsLiveInventory | null = null;
  private readonly releasedStockers = new Map<string, AmhsLiveInventoryStocker>();
  private readonly releasedGates = new Map<string, AmhsLiveInventoryGate>();
  private readonly listeners = new Set<Listener>();
  private lastRuntimeRecomputeMs = 0;

  constructor() {
    const scheduleRecompute = () => {
      const now = performance.now();
      if (now - this.lastRuntimeRecomputeMs < RUNTIME_RECOMPUTE_INTERVAL_MS) return;
      this.lastRuntimeRecomputeMs = now;
      this.recompute();
    };
    liveOhtRuntimeStore.subscribe(scheduleRecompute);
    transferAnimationPipelineStore.subscribe(() => this.recompute());
  }

  setRaw(inventory: AmhsLiveInventory | null): void {
    if (this.raw === inventory) return;
    this.raw = inventory;
    this.recompute();
  }

  setLastStockerDrops(lastStockerDrops: Record<string, AmhsStockerDropSummary>): void {
    if (this.lastStockerDrops === lastStockerDrops) return;
    this.lastStockerDrops = lastStockerDrops;
    this.recompute();
  }

  getSnapshot(): AmhsLiveInventory | null {
    return this.display;
  }

  subscribe(listener: Listener): () => void {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  }

  reset(): void {
    this.raw = null;
    this.lastStockerDrops = {};
    this.display = null;
    this.releasedStockers.clear();
    this.releasedGates.clear();
    this.lastRuntimeRecomputeMs = 0;
    this.notify();
  }

  private recompute(): void {
    const targets = resolveInventoryHoldTargets(liveOhtRuntimeStore.getFrame(), this.raw, {
      ohtTimelines: liveMotionStore.getOhtTimelines(),
      lastStockerDrops: this.lastStockerDrops,
      releasedStockers: this.releasedStockers,
    });
    const next = applyInventoryHold(
      this.raw,
      targets,
      this.releasedStockers,
      this.releasedGates
    );
    if (sameInventorySnapshot(this.display, next)) return;
    this.display = next;
    this.notify();
  }

  private notify(): void {
    for (const listener of this.listeners) listener();
  }
}

export const liveInventoryDisplayStore = new LiveInventoryDisplayStore();
